import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    DisplayMode,
    ReportTemplateDto,
    ReportTemplateJson,
    ReportTemplateType,
    SortModel,
} from "../models/reporting/reportTemplate";
import { getReportTemplates } from "../services/reportTemplateService";
import { Base } from "../framework/base";
import { GroupingValue } from "./reportOptionsSlice";
import { getRandomKey } from "../hooks/useReload";

export interface ReportTemplateState {
    reloadKey: number;
    templates: Record<ReportTemplateType, ReportTemplateDto[]>;
    selected: Record<ReportTemplateType, string>;
    loading: boolean;
    current: ReportTemplateJson;
    hasChanges: boolean;
}

export const initialState: ReportTemplateState = {
    reloadKey: getRandomKey(),
    templates: { [ReportTemplateType.WorkTimeReport]: null },
    selected: { [ReportTemplateType.WorkTimeReport]: null },
    loading: false,
    hasChanges: false,
    current: {
        timeRange: [
            Base.dayjsToJsonDate(
                Base.dateStartOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
            Base.dayjsToJsonDate(
                Base.dateEndOf(Base.dateAddMonths(new Date(), -1), "month")
            ),
        ],
        filters: {},
        selectedColumns: ["duration"],
        columnOrder: ["duration"],
        sortModel: [],
        grouping: [GroupingValue.Employee],
        groupByRow: false,
        groupingModeMultiple: false,
        showGroupedRows: true,
        display: {
            mode: DisplayMode.Decimals,
            decimals: 2,
        },
    },
};

export const reportTemplateSlice = createSlice({
    name: "reportTemplate",
    initialState,
    reducers: {
        reloadReportTemplates: (state) => {
            state.reloadKey = getRandomKey();
        },
        setReportTemplatesLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setReportTemplates: (
            state,
            action: PayloadAction<{
                type: ReportTemplateType;
                templates: ReportTemplateDto[];
            }>
        ) => {
            state.templates[action.payload.type] = action.payload.templates;
            state.loading = false;
            state.hasChanges = false;
        },
        setSelectedReportTemplate: (
            state,
            action: PayloadAction<{
                type: ReportTemplateType;
                id: string;
            }>
        ) => {
            state.selected[action.payload.type] = action.payload.id;
        },
        setCurrentReportTemplate: (
            state,
            action: PayloadAction<ReportTemplateJson>
        ) => {
            const template = { ...action.payload };

            const diffInMonths = Base.dayjsDiffInMonths(
                template.timeRange[0],
                Base.dateAddDays(template.timeRange[1], 1)
            );

            const diffIsMonths = diffInMonths > 0 && diffInMonths % 1 === 0;

            if (diffIsMonths) {
                const endDate = Base.dayjsToJsonDate(
                    Base.dateEndOf(Base.dateAddMonths(new Date(), -1), "month")
                );
                const startDate = Base.dayjsToJsonDate(
                    Base.dateStartOf(
                        Base.dateAddMonths(endDate, -diffInMonths + 1),
                        "month"
                    )
                );

                template.timeRange = [startDate, endDate];
            } else {
                const diffInWeeks = Base.dayjsDiffInWeeks(
                    template.timeRange[0],
                    Base.dateAddDays(template.timeRange[1], 1)
                );

                const diffIsWeeks = diffInWeeks > 0 && diffInWeeks % 1 === 0;

                if (diffIsWeeks) {
                    const endDate = Base.dayjsToJsonDate(
                        Base.dateEndOf(
                            Base.dateAddWeeks(new Date(), -1),
                            "week"
                        )
                    );
                    const startDate = Base.dayjsToJsonDate(
                        Base.dateStartOf(
                            Base.dateAddWeeks(endDate, -diffInWeeks + 1),
                            "week"
                        )
                    );

                    template.timeRange = [startDate, endDate];
                }
            }

            state.current = template;

            // Fill in missing propertios
            Object.keys(initialState.current).forEach((k) => {
                if (state.current[k] === undefined) {
                    const initialValue = initialState.current[k];

                    if (typeof initialValue === "object") {
                        if (Array.isArray(initialValue)) {
                            state.current[k] = [...initialValue];
                        } else {
                            state.current[k] = { ...initialValue };
                        }
                    } else {
                        state.current[k] = initialValue;
                    }
                }
            });

            state.hasChanges = false;
        },
        clearReportFilters: (state) => {
            state.current.filters = { ...initialState.current.filters };
            state.hasChanges = true;
        },
        setReportTimeRange(state, action: PayloadAction<[string, string]>) {
            const currentDiffInMonths = Base.dayjsDiffInMonths(
                state.current.timeRange[0],
                Base.dateAddDays(state.current.timeRange[1], 1)
            );
            const newDiffInMonths = Base.dayjsDiffInMonths(
                action.payload[0],
                Base.dateAddDays(action.payload[1], 1)
            );
            const diffIsMonths =
                currentDiffInMonths > 0 && currentDiffInMonths % 1 === 0;
            const newDiffIsMonths =
                newDiffInMonths > 0 && newDiffInMonths % 1 === 0;

            // If period is changed from or to months,
            // or amount of months is different,
            // or if period is not month and the amount of days has changed.
            // then template has been modified.
            if (
                diffIsMonths !== newDiffIsMonths ||
                currentDiffInMonths !== newDiffInMonths ||
                (!diffIsMonths &&
                    Base.dayjsDiffInDays(
                        state.current.timeRange[0],
                        state.current.timeRange[1]
                    ) !==
                        Base.dayjsDiffInDays(
                            action.payload[0],
                            action.payload[1]
                        ))
            ) {
                state.hasChanges = true;
            }

            state.current.timeRange = action.payload;
        },
        setReportSelectedVehicles(state, action: PayloadAction<string[]>) {
            state.current.filters.selectedVehicles = action.payload;
            state.hasChanges = true;
        },
        setReportSelectedCostCenters(state, action: PayloadAction<string[]>) {
            state.current.filters.selectedCostCenters = action.payload;
            state.hasChanges = true;
        },
        setReportSelectedEmployees(state, action: PayloadAction<string[]>) {
            state.current.filters.selectedEmployees = action.payload;
            state.hasChanges = true;
        },
        setReportSelectedEmployeeGroups(
            state,
            action: PayloadAction<string[]>
        ) {
            state.current.filters.selectedEmployeeGroups = action.payload;
            state.hasChanges = true;
        },
        setReportSelectedCustomers(state, action: PayloadAction<string[]>) {
            state.current.filters.selectedCustomers = action.payload;
            state.hasChanges = true;
        },
        setReportSelectedWorkTimeTypes(state, action: PayloadAction<string[]>) {
            state.current.filters.selectedWorkTimeTypes = action.payload;
            state.hasChanges = true;
        },
        setReportTemplateGrouping: (state, action: PayloadAction<string[]>) => {
            state.current.grouping = action.payload.filter(Boolean);
            state.hasChanges = true;

            if (state.current.grouping.includes("employeeId")) {
                state.current.selectedColumns =
                    state.current.selectedColumns.filter(
                        (c) => !c.includes("e_")
                    );
            }

            if (state.current.grouping.includes("vehicleId")) {
                state.current.selectedColumns =
                    state.current.selectedColumns.filter(
                        (c) => !c.includes("v_")
                    );
            }

            if (state.current.grouping.includes("customerId")) {
                state.current.selectedColumns =
                    state.current.selectedColumns.filter(
                        (c) => !c.includes("c_")
                    );
            }

            if (state.current.grouping.includes("costCenterId")) {
                state.current.selectedColumns =
                    state.current.selectedColumns.filter(
                        (c) => !c.includes("cc_")
                    );
            }

            if (state.current.grouping.includes("workTimeTypeId")) {
                state.current.selectedColumns =
                    state.current.selectedColumns.filter(
                        (c) => !c.includes("wtt_")
                    );
            }
        },
        setReportTemplateGroupByRow: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.current.groupByRow = action.payload;
            state.hasChanges = true;
        },
        setReportTemplateGroupingModeMultiple: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.current.groupingModeMultiple = action.payload;
            state.hasChanges = true;
        },
        setReportTemplateShowGroupedRows: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.current.showGroupedRows = action.payload;
            state.hasChanges = true;
        },
        setReportTemplateSelectedColumns: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.current.selectedColumns = action.payload;
            state.hasChanges = true;
        },
        setReportTemplateColumnOrder: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.current.columnOrder = action.payload;
            state.hasChanges = true;
        },
        setReportSortModel: (state, action: PayloadAction<SortModel[]>) => {
            state.current.sortModel = action.payload;
            state.hasChanges = true;
        },
        setReportDisplayMode: (state, action: PayloadAction<DisplayMode>) => {
            state.current.display.mode = action.payload;
            state.hasChanges = true;
        },
        setReportDisplayDecimals: (state, action: PayloadAction<number>) => {
            state.current.display.decimals = action.payload;
            state.hasChanges = true;
        },
    },
});

export const {
    reloadReportTemplates,
    setReportTemplatesLoading,
    setReportTemplates,
    setSelectedReportTemplate,
    setCurrentReportTemplate,
    clearReportFilters,
    setReportTimeRange,
    setReportSelectedVehicles,
    setReportSelectedCostCenters,
    setReportSelectedEmployees,
    setReportSelectedEmployeeGroups,
    setReportSelectedCustomers,
    setReportSelectedWorkTimeTypes,
    setReportTemplateGrouping,
    setReportTemplateGroupByRow,
    setReportTemplateGroupingModeMultiple,
    setReportTemplateShowGroupedRows,
    setReportTemplateSelectedColumns,
    setReportTemplateColumnOrder,
    setReportSortModel,
    setReportDisplayMode,
    setReportDisplayDecimals,
} = reportTemplateSlice.actions;

export const fetchReportTemplates = createAsyncThunk(
    "reportTemplate/list",
    async({ type }: { type: ReportTemplateType }, thunkApi) => {
        thunkApi.dispatch(setReportTemplatesLoading(true));
        const templates = await getReportTemplates(type, thunkApi.signal);
        thunkApi.dispatch(setReportTemplates({ type, templates }));
    }
);

export const reportTemplateReducer = reportTemplateSlice.reducer;
